import { Divider, Typography } from "@drone-js/core";
import { VStack } from "apps/commons/components/Stack";
import type { PropertyDataRateEnum } from "libs/codegen/property-onboarding";
import { Fragment, type FunctionComponent } from "react";
import { useController, useFormContext, useWatch } from "react-hook-form";
import { RadioButton } from "../../commons/components/RadioButton";
import { rateMapper } from "../../oneOnboarding/pricing/constants";
import { useStaticDataAtomValue } from "../../oneOnboarding/staticDataAtom";
import type { BulkPropertyFormData } from "../BulkPropertyFormData";
import { BatchChannelManagerPriceController } from "./BatchChannelManagerPriceController";
import { BatchFirstCheckInDateController } from "./BatchFirstCheckInDateController";
import { PropertyPricingList } from "./PropertyPricingList";

interface Props {
    isFirstOpenAllotmentDate?: boolean;
}

export const BatchRateController: FunctionComponent<Props> = ({ isFirstOpenAllotmentDate = false }) => {
    const {
        cmsData: {
            pricing: {
                howWillYouManageYourRate,
                setRateManually,
                manageYourNightlyRateDirectlyThroughAgoda,
                connectWithChannelManager,
                manageYourRateAndAvailabilityThroughAThirdParty,
                howToManageRateAndAvailability,
                manageYourselfDescription,
            },
        },
    } = useStaticDataAtomValue();

    const { control, getValues, setValue, trigger } = useFormContext<BulkPropertyFormData>();
    const properties =
        (useWatch<BulkPropertyFormData>({
            control,
            name: "propertyDataList",
        }) as BulkPropertyFormData["propertyDataList"]) ?? [];

    const isAllPropertiesManual =
        properties.length > 0 && properties.every((property) => property.rate === rateMapper.Manual);
    const isAllPropertiesChannelManager =
        properties.length > 0 && properties.every((property) => property.rate === rateMapper.ChannelManager);
    const hasRateControllerContent = isAllPropertiesManual || isAllPropertiesChannelManager;
    const {
        fieldState: { error: rateError },
    } = useController({
        control,
        name: "propertyDataList.0.rate",
        rules: {
            validate: () => {
                const selectedProperties = getValues("propertyDataList") ?? [];
                const hasManualRate = selectedProperties.every((property) => property.rate === rateMapper.Manual);
                const hasChannelManagerRate = selectedProperties.every(
                    (property) => property.rate === rateMapper.ChannelManager,
                );

                return (
                    !isFirstOpenAllotmentDate ||
                    (selectedProperties.length > 0 && (hasManualRate || hasChannelManagerRate)) ||
                    howToManageRateAndAvailability
                );
            },
        },
    });
    const handleSetRate = (rate: PropertyDataRateEnum) => () => {
        properties.forEach((_, i) => {
            setValue(`propertyDataList.${i}.rate`, rate, { shouldDirty: true, shouldValidate: true });

            if (rate === rateMapper.ChannelManager) {
                setValue(`propertyDataList.${i}.firstAllotment`, null, { shouldDirty: true });
                setValue(`propertyDataList.${i}.firstAllotmentDate`, null, { shouldDirty: true });
            }
        });
        void trigger("propertyDataList.0.rate");
    };

    return (
        <Fragment>
            <Typography variant="heading-default">
                {isFirstOpenAllotmentDate ? howToManageRateAndAvailability : howWillYouManageYourRate}
            </Typography>
            <VStack gap="16" data-testid="rate-options">
                <RadioButton
                    id={rateMapper.Manual}
                    title={setRateManually}
                    description={
                        isFirstOpenAllotmentDate ? manageYourselfDescription : manageYourNightlyRateDirectlyThroughAgoda
                    }
                    dataElementName="set-rate-manually"
                    dataTestId="rate-option-manual"
                    isSelected={isAllPropertiesManual}
                    setValue={handleSetRate(rateMapper.Manual)}
                />
                {isAllPropertiesManual && isFirstOpenAllotmentDate && <BatchFirstCheckInDateController />}
                <RadioButton
                    id={rateMapper.ChannelManager}
                    title={connectWithChannelManager}
                    description={manageYourRateAndAvailabilityThroughAThirdParty}
                    dataElementName="set-channel-manager"
                    dataTestId="rate-option-channel-manager"
                    isSelected={isAllPropertiesChannelManager}
                    setValue={handleSetRate(rateMapper.ChannelManager)}
                />
                {rateError && (
                    <Typography variant="body-s" textColor="feedback.negative">
                        {rateError.message}
                    </Typography>
                )}
            </VStack>
            {hasRateControllerContent && (
                <>
                    <Divider />
                    <VStack gap="16" data-testid="rate-controller-content">
                        {isAllPropertiesManual && <PropertyPricingList />}
                        {isAllPropertiesChannelManager && (
                            <BatchChannelManagerPriceController triggerChangeOnAutoSelect={isFirstOpenAllotmentDate} />
                        )}
                    </VStack>
                </>
            )}
        </Fragment>
    );
};
